import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import PruloLogo from "@/components/shared/PruloLogo";
import Magnetic from "@/components/shared/Magnetic";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
  { to: "/become-a-creator", label: "Creators" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-black/60 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center group">
          <PruloLogo height={44} className="md:!h-14" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-full transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Magnetic radius={110} strength={0.35}>
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="hidden md:inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-zinc-200 btn-magnetic glow-cta"
            >
              Book Strategy Call
            </Link>
          </Magnetic>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-nav-panel" className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur-2xl">
          <div className="px-6 py-6 flex flex-col gap-2">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-base py-2 ${isActive ? "text-white" : "text-zinc-400"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-3 bg-white text-black text-sm font-semibold px-4 py-3 rounded-full text-center">
              Book Strategy Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
